import type { Sim } from '@/types'

import { palette } from '@/constants/palette'

const GRAVITY = 9.81
const STEP = 1 / 240
const TIME_SCALE = 0.8
const TRAIL_LENGTH = 240

// Equal masses and equal rod lengths; returns the two angular accelerations.
const accelerations = (t1: number, t2: number, w1: number, w2: number): [number, number] => {
    const delta = t1 - t2
    const denominator = 3 - Math.cos(2 * delta)
    const a1 = (-3 * GRAVITY * Math.sin(t1) - GRAVITY * Math.sin(t1 - 2 * t2) - 2 * Math.sin(delta) * (w2 * w2 + w1 * w1 * Math.cos(delta))) / denominator
    const a2 = (2 * Math.sin(delta) * (2 * w1 * w1 + 2 * GRAVITY * Math.cos(t1) + w2 * w2 * Math.cos(delta))) / denominator

    return [a1, a2]
}

export const createDoublePendulum = (): Sim => {
    let t1 = 2.2
    let t2 = 2.9
    let w1 = 0
    let w2 = 0
    let accumulator = 0
    const trail: number[] = []

    const integrate = (h: number) => {
        const [k1w1, k1w2] = accelerations(t1, t2, w1, w2)
        const k1t1 = w1
        const k1t2 = w2

        const [k2w1, k2w2] = accelerations(t1 + k1t1 * h / 2, t2 + k1t2 * h / 2, w1 + k1w1 * h / 2, w2 + k1w2 * h / 2)
        const k2t1 = w1 + k1w1 * h / 2
        const k2t2 = w2 + k1w2 * h / 2

        const [k3w1, k3w2] = accelerations(t1 + k2t1 * h / 2, t2 + k2t2 * h / 2, w1 + k2w1 * h / 2, w2 + k2w2 * h / 2)
        const k3t1 = w1 + k2w1 * h / 2
        const k3t2 = w2 + k2w2 * h / 2

        const [k4w1, k4w2] = accelerations(t1 + k3t1 * h, t2 + k3t2 * h, w1 + k3w1 * h, w2 + k3w2 * h)
        const k4t1 = w1 + k3w1 * h
        const k4t2 = w2 + k3w2 * h

        t1 += (h / 6) * (k1t1 + 2 * k2t1 + 2 * k3t1 + k4t1)
        t2 += (h / 6) * (k1t2 + 2 * k2t2 + 2 * k3t2 + k4t2)
        w1 += (h / 6) * (k1w1 + 2 * k2w1 + 2 * k3w1 + k4w1)
        w2 += (h / 6) * (k1w2 + 2 * k2w2 + 2 * k3w2 + k4w2)
    }

    return {
        resize: () => {},

        update: (dt: number) => {
            accumulator += dt * TIME_SCALE

            while (accumulator >= STEP) {
                integrate(STEP)
                accumulator -= STEP
            }

            trail.push(Math.sin(t1) + Math.sin(t2), Math.cos(t1) + Math.cos(t2))

            if (trail.length > TRAIL_LENGTH * 2) trail.splice(0, trail.length - TRAIL_LENGTH * 2)
        },

        draw: (ctx: CanvasRenderingContext2D, width: number, height: number) => {
            const scale = Math.min(width, height) * 0.205
            const pivotX = width / 2
            const pivotY = height * 0.47

            ctx.save()
            ctx.lineCap = 'round'
            ctx.lineJoin = 'round'

            ctx.setLineDash([2, 5])
            ctx.strokeStyle = palette.faint
            ctx.lineWidth = 1
            ctx.beginPath()
            ctx.arc(pivotX, pivotY, scale * 2, 0, Math.PI * 2)
            ctx.stroke()
            ctx.setLineDash([])

            const points = trail.length / 2

            for (let index = 1; index < points; index++) {
                const progress = index / points

                ctx.strokeStyle = `rgba(47, 73, 255, ${(progress * progress * 0.85).toFixed(3)})`
                ctx.lineWidth = 0.6 + progress * 1.8
                ctx.beginPath()
                ctx.moveTo(pivotX + trail[(index - 1) * 2] * scale, pivotY + trail[(index - 1) * 2 + 1] * scale)
                ctx.lineTo(pivotX + trail[index * 2] * scale, pivotY + trail[index * 2 + 1] * scale)
                ctx.stroke()
            }

            const x1 = pivotX + Math.sin(t1) * scale
            const y1 = pivotY + Math.cos(t1) * scale
            const x2 = x1 + Math.sin(t2) * scale
            const y2 = y1 + Math.cos(t2) * scale

            ctx.strokeStyle = palette.ink
            ctx.lineWidth = 1.75
            ctx.beginPath()
            ctx.moveTo(pivotX, pivotY)
            ctx.lineTo(x1, y1)
            ctx.lineTo(x2, y2)
            ctx.stroke()

            ctx.fillStyle = palette.paper
            ctx.strokeStyle = palette.ink
            ctx.lineWidth = 1.5
            ctx.beginPath()
            ctx.arc(pivotX, pivotY, 4, 0, Math.PI * 2)
            ctx.fill()
            ctx.stroke()

            ctx.fillStyle = palette.ink
            ctx.beginPath()
            ctx.arc(x1, y1, 6.5, 0, Math.PI * 2)
            ctx.fill()

            ctx.fillStyle = palette.brand
            ctx.strokeStyle = palette.paper
            ctx.lineWidth = 2.5
            ctx.beginPath()
            ctx.arc(x2, y2, 8, 0, Math.PI * 2)
            ctx.fill()
            ctx.stroke()

            ctx.restore()
        }
    }
}
